/**
 * COSMIC 9 GEMS — REVEAL ON SCROLL
 *
 * One IntersectionObserver for the whole document. Elements opt in with a
 * data attribute; the CSS in c9-components.css owns every visual detail.
 *
 *   <div data-c9-reveal>          fade
 *   <div data-c9-reveal="up">     fade and rise
 *   <div data-c9-reveal="image">  settle into frame (scale)
 *
 *   <ul data-c9-reveal-group>     stagger direct children
 *     <li data-c9-reveal="up">…</li>
 *   </ul>
 *
 * Safety contract
 *
 * The hidden state in CSS is scoped to `.c9-reveal-ready` on <html>, and this
 * file's first statement is what adds it. If the asset 404s, is blocked by an
 * extension, or throws before this point, nothing is ever hidden and the page
 * renders static. Content visibility is never allowed to depend on a script
 * succeeding.
 *
 * Under prefers-reduced-motion the class is never added at all, so there is no
 * observer, no transition and no work — not merely a faster animation.
 */

(function () {
  'use strict';

  var REVEAL_SELECTOR = '[data-c9-reveal]';
  var GROUP_SELECTOR = '[data-c9-reveal-group]';
  var READY_CLASS = 'c9-reveal-ready';

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // No observer support and no motion preference both resolve the same way:
  // leave the document untouched and let everything render.
  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    return;
  }

  document.documentElement.classList.add(READY_CLASS);

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    {
      // The bottom margin fires a little before the element reaches the fold,
      // so the transition has finished by the time the shopper looks at it.
      //
      // The enormous TOP margin is not a typo. With a 0px top, an element that
      // is jumped past — an anchor link, a restored scroll position, a fast
      // flick — goes from "below the viewport, not intersecting" straight to
      // "above the viewport, not intersecting" without ever crossing the
      // threshold. The callback never fires and the content stays at opacity 0
      // permanently. Expanding the root upwards means anything at or above the
      // viewport counts as intersecting, so the only way to stay hidden is to
      // be genuinely below the fold. No scroll listener needed.
      rootMargin: '9999px 0px -8% 0px',
      threshold: 0,
    }
  );

  /**
   * Assign the stagger index to a group's children. Done here rather than in
   * Liquid so that groups built by JS — filtered results, load-more pages,
   * recommendation output — stagger for free.
   */
  function indexGroup(group) {
    var index = 0;
    Array.prototype.forEach.call(group.children, function (child) {
      if (!child.hasAttribute('data-c9-reveal')) return;
      child.style.setProperty('--c9-reveal-i', String(index));
      index += 1;
    });
  }

  /**
   * Register everything inside `root` that is not already registered.
   * Idempotent, so it is safe to call repeatedly.
   */
  function scan(root) {
    var scope = root || document;

    if (scope.matches && scope.matches(GROUP_SELECTOR)) indexGroup(scope);
    Array.prototype.forEach.call(
      scope.querySelectorAll(GROUP_SELECTOR),
      indexGroup
    );

    var candidates = [];
    if (scope.matches && scope.matches(REVEAL_SELECTOR)) candidates.push(scope);
    Array.prototype.push.apply(
      candidates,
      scope.querySelectorAll(REVEAL_SELECTOR)
    );

    candidates.forEach(function (element) {
      if (element.dataset.c9RevealBound === 'true') return;
      element.dataset.c9RevealBound = 'true';

      // Anything already on screen at first paint is revealed immediately and
      // never observed. Animating the hero on load is the cheapest-looking
      // thing a site can do.
      var box = element.getBoundingClientRect();
      var inViewport = box.top < window.innerHeight && box.bottom > 0;

      if (inViewport && !element.hasAttribute('data-c9-reveal-always')) {
        element.classList.add('is-revealed');
        return;
      }

      observer.observe(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  // The theme editor swaps section markup without a page load, and Phase C's
  // filtering and load-more will replace grid contents the same way.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  // Exposed so any c9 script that injects markup can register it without
  // needing to know how reveal works.
  window.C9Reveal = { scan: scan };
})();
